import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Snackbar, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InboxIcon from '@mui/icons-material/Inbox';
import ReplayIcon from '@mui/icons-material/Replay';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { formatDate } from '../../utils';
import DataTable from '../shared/DataTable';
import usePaginatedList from '../../hooks/usePaginatedList';
import { PL_API, purchaseErrorMessage } from './purchaseUtils';

const STATUS_COLOR = { new: 'info', processing: 'info', processed: 'success', failed: 'error', ignored: 'default' };

/** Inbox items that did not (yet) become an invoice: failed, ignored, waiting. */
function PurchaseInboxView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const canManage = can(currentOrganization, 'purchase_invoices.manage');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [busyId, setBusyId] = useState(null);

  const list = usePaginatedList(`${PL_API}/inbox/`, {
    initialFilters: { status: 'new,processing,failed,ignored' },
    defaultPageSize: 50,
  });

  const act = async (item, action) => {
    setBusyId(item.id);
    try {
      const response = await axios.post(`${PL_API}/inbox/${item.id}/${action}/`, {});
      if (action === 'convert' && response.data?.invoice_id) {
        navigate(`/ostolaskut/${response.data.invoice_id}`);
        return;
      }
      setSnackbar({ open: true, message: t(`purchaseLedger.inbox.${action}Done`), severity: 'success' });
      list.refetch();
    } catch (error) {
      setSnackbar({ open: true, message: purchaseErrorMessage(t, error), severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: 'created_at',
      label: t('purchaseLedger.inbox.received'),
      width: '1%',
      render: (r) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {formatDate(r.created_at)}
        </Typography>
      ),
    },
    {
      key: 'source',
      label: t('purchaseLedger.inbox.source'),
      width: '1%',
      render: (r) => <Chip size="small" variant="outlined" label={t(`purchaseLedger.source.${r.source}`, { defaultValue: r.source })} />,
    },
    {
      key: 'filename',
      label: t('purchaseLedger.inbox.file'),
      maxWidth: 280,
      render: (r) => (
        <Box>
          <Typography variant="body2" noWrap>
            {r.filename || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {[r.sender_email, r.subject].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: t('purchaseLedger.fields.status'),
      width: '1%',
      render: (r) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Chip size="small" color={STATUS_COLOR[r.status] || 'default'} label={t(`purchaseLedger.inbox.status.${r.status}`, { defaultValue: r.status })} sx={{ width: 'fit-content' }} />
          {r.reason_code && (
            <Typography variant="caption" color="text.secondary">
              {t(`purchaseLedger.inbox.reasons.${r.reason_code}`, { defaultValue: r.reason_code })}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '1%',
      render: (r) => {
        if (!canManage) return null;
        const busy = busyId === r.id;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
            {(r.status === 'failed' || r.status === 'new') && (
              <Button size="small" startIcon={<ReplayIcon />} disabled={busy} onClick={(e) => { e.stopPropagation(); act(r, 'retry'); }} data-testid={`pl-inbox-retry-${r.id}`}>
                {t('purchaseLedger.inbox.retry')}
              </Button>
            )}
            {r.status !== 'processed' && (
              <Button size="small" disabled={busy} onClick={(e) => { e.stopPropagation(); act(r, 'convert'); }} data-testid={`pl-inbox-convert-${r.id}`}>
                {t('purchaseLedger.inbox.convert')}
              </Button>
            )}
            {r.status !== 'ignored' && r.status !== 'processed' && (
              <Button size="small" color="inherit" disabled={busy} onClick={(e) => { e.stopPropagation(); act(r, 'ignore'); }} data-testid={`pl-inbox-ignore-${r.id}`}>
                {t('purchaseLedger.inbox.ignore')}
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="purchase-inbox-page">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ostolaskut')} sx={{ mb: 1 }}>
        {t('purchaseLedger.backToList')}
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <InboxIcon color="secondary" />
        <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
          {t('purchaseLedger.inbox.title')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('purchaseLedger.inbox.subtitle')}
      </Typography>
      <DataTable
        columns={columns}
        data={list.data}
        loading={list.loading}
        emptyIcon={<InboxIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
        emptyTitle={t('purchaseLedger.inbox.empty')}
        emptyDescription={t('purchaseLedger.inbox.emptyHint')}
        pagination={{
          page: list.page,
          totalPages: list.totalPages,
          totalCount: list.totalCount,
          pageSize: list.pageSize,
          onPageChange: list.setPage,
          onPageSizeChange: list.setPageSize,
        }}
        testIdPrefix="pl-inbox-row"
      />
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PurchaseInboxView;
